import { useCallback, useEffect, useRef, useState } from "react";

import { LOB_IDS } from "@/shared/common/static";
import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

function readPreQuoteSnapshot(refs: Refs, lobId: number) {
  if (lobId === LOB_IDS.TRAVEL) {
    return JSON.stringify({
      personal: refs.travelPersonalRef.current?.getValues(),
      details: refs.travelDetailsRef.current?.getValues(),
    });
  }
  if (lobId === LOB_IDS.LIFE) {
    return JSON.stringify({
      personal: refs.lifePersonalRef.current?.getValues(),
      planDetails: refs.lifePlanDetailsRef.current?.getValues(),
    });
  }
  if (lobId === LOB_IDS.MOTOR) {
    return JSON.stringify({
      personal: refs.carPersonalRef.current?.getValues(),
      vehicle: refs.carVehicleRef.current?.getValues(),
      driver: refs.carDriverRef.current?.getValues(),
    });
  }
  if (lobId === LOB_IDS.HEALTH) {
    return JSON.stringify({
      personal: refs.healthPersonalRef.current?.getValues(),
      details: refs.healthDetailsRef.current?.getValues(),
      habits: refs.healthHabitsRef.current?.getValues(),
      quickQuestions: refs.healthQuickQuestionsRef.current?.getValues(),
    });
  }
  return "";
}

function isSnapshotReady(snapshot: string) {
  return Boolean(snapshot) && snapshot !== "{}";
}

export function usePreQuoteFormChange(refs: Refs, lobId: number, resetKey: string) {
  const refsRef = useRef(refs);
  refsRef.current = refs;

  const baselineRef = useRef<string | null>(null);
  const [hasChanged, setHasChanged] = useState(false);

  const captureBaseline = useCallback(() => {
    const snapshot = readPreQuoteSnapshot(refsRef.current, lobId);
    if (!isSnapshotReady(snapshot)) return;
    baselineRef.current = snapshot;
    setHasChanged(false);
  }, [lobId]);

  useEffect(() => {
    baselineRef.current = null;
    setHasChanged(false);

    const timer = window.setTimeout(() => {
      captureBaseline();
    }, 200);

    return () => window.clearTimeout(timer);
  }, [captureBaseline, resetKey]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const baseline = baselineRef.current;
      if (!baseline) return;

      const current = readPreQuoteSnapshot(refsRef.current, lobId);
      if (!isSnapshotReady(current)) return;

      setHasChanged(current !== baseline);
    }, 300);

    return () => window.clearInterval(interval);
  }, [lobId, resetKey]);

  return {
    hasPreQuoteChanged: hasChanged,
    capturePreQuoteBaseline: captureBaseline,
  };
}
